import React, {
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import { Collapse, List, Skeleton } from 'antd'
import { SyncChartPointer } from 'metrics-chart'
import '@elastic/charts/dist/theme_only_light.css'
import { I18nContext, useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import useSWR from 'swr'

import { ClusterCluster } from '@clinic/client'
import { getClusterInfo, getReport } from '~/api'
import LoadingSpin from '~/components/LoadingSpin'
import { Header } from '~/componentv2/Header'
import { ReportPVType } from '~/pages/reports/shared/utils/pv'
import { RemoteNamespace } from '~/translations'
import { loadYAML } from '~/utils/load'

import { ErrorMsg } from '../../shared/components/Error'
import {
  Advice,
  MetricStats,
  PromQLSchema,
  ReportArgs
} from '../../shared/types'
import { usePrevPath } from '../../shared/utils/prev_path'
import { reports } from '../schema'
import { BENCHMARK_REPORT_NS } from '../translation'
import { BenchmarkReport, BenchmarkReportStats } from '../types'
import { AdviceSummary } from './AdviceSummary'
import { BasicInfo } from './BasicInfo'
import { MetricCategory } from './MetricCategory'
import { Topology } from './Topology'

import '../translation'
import './index.css'

export type PromqlSchemaMap = { [key: string]: PromQLSchema }
export type MetricStatsMap = { [metric: string]: BenchmarkReportStats }
export type AdviceMap = { [cate: string]: Advice[] }

const ReportPageWrapper: React.FC = () => {
  const [promqlSchema, setPromqlSchema] = useState<PromqlSchemaMap | null>(null)
  const { i18n } = useContext(I18nContext) || {}

  // Load i18n remote resources for health report
  useEffect(() => {
    i18n?.loadNamespaces([RemoteNamespace.Metrics])
  }, [i18n])

  useEffect(() => {
    loadYAML('metrics-advisor/promql').then((d: any) => {
      setPromqlSchema(
        (d as PromQLSchema[]).reduce((prev, cur) => {
          prev[cur.name] = cur
          return prev
        }, {} as { [key: string]: PromQLSchema })
      )
    })
  }, [])

  return (
    <Suspense fallback={<LoadingSpin />}>
      <Header i18n />
      {promqlSchema ? (
        <SyncChartPointer>
          <Page promqlSchema={promqlSchema} />
        </SyncChartPointer>
      ) : (
        <>Loading...</>
      )}
    </Suspense>
  )
}

interface BenchmarkReportProps {
  promqlSchema: PromqlSchemaMap
}

const Page: React.FC<BenchmarkReportProps> = ({ promqlSchema }) => {
  const { t } = useTranslation(BENCHMARK_REPORT_NS)
  const { orgId, cid, rid } = useParams<'orgId' | 'cid' | 'rid'>()
  const { data: clusterInfoRes, error: clusterError } = useSWR(
    `/orgs/${orgId}/clusters/${cid}`,
    () => getClusterInfo(orgId!, cid!)
  )
  const isClusterLoading = !clusterInfoRes && !clusterError
  const { data: reportRes, error: reportError } = useSWR(
    `/reports/benchmark/${rid}`,
    () => getReport(orgId!, cid!, rid!)
  )
  const isReportLoading = !reportRes && !reportError
  const isLoading = isClusterLoading || isReportLoading
  const prevPath = usePrevPath()
  const reportData = reportRes?.data?.data as any
  const reportArgs = reportRes?.data?.args as ReportArgs
  const adviceList = useMemo<AdviceMap>(() => {
    if (!reportData?.advice) {
      return {}
    }
    return (reportData.advice as Advice[]).reduce((prev, cur) => {
      const cate = cur.category[0]
      if (!prev[cate]) {
        prev[cate] = [cur]
      } else {
        prev[cate].push(cur)
      }
      return prev
    }, {} as AdviceMap)
  }, [reportData])
  const metricStats = useMemo<MetricStatsMap>(() => {
    if (!reportData?.metricStats) {
      return {}
    }
    return (reportData.metricStats as MetricStats[]).reduce((prev, cur) => {
      prev[cur.name] = cur
      return prev
    }, {} as MetricStatsMap)
  }, [reportData])

  return (
    <div className='m-auto max-w-[1440px]'>
      <h1 className='p-4 mt-4'>{t('title')}</h1>
      <div className='m-4 p-4 bg-white'>
        {isLoading ? (
          <Skeleton active />
        ) : !!reportError ? (
          <ErrorMsg
            title={`${reportError.toString()}: ${
              reportError?.response?.data?.message
            }`}
            backTo={prevPath}
          />
        ) : (
          <Collapse
            defaultActiveKey={[
              'basic',
              'topology',
              'advice_summary',
              ...reports.map((r) => r.title)
            ]}
            ghost
          >
            <Collapse.Panel
              key='basic'
              className='mb-6'
              header={<h3 className='mb-0'>Basic Info</h3>}
            >
              <BasicInfo
                reportType={ReportPVType.Benchmark}
                report={reportRes?.data as any}
                clusterData={clusterInfoRes?.data}
              />
            </Collapse.Panel>
            <Collapse.Panel
              key='topology'
              className='mb-6'
              header={<h3 className='mb-0'>Topology</h3>}
            >
              <Topology metricStats={metricStats} />
            </Collapse.Panel>
            <Collapse.Panel
              key='advice_summary'
              className='mb-6'
              header={<h3 className='mb-0'>Suggestion Summary</h3>}
            >
              <AdviceSummary
                advices={adviceList}
                promqlSchema={promqlSchema}
                reportArgs={reportArgs}
                cluster={clusterInfoRes?.data}
              />
            </Collapse.Panel>

            {reports.map((report) => (
              <Collapse.Panel
                key={report.title}
                header={<h3 className='mb-0'>{report.title}</h3>}
              >
                <MetricPanel
                  report={report}
                  promqlSchema={promqlSchema}
                  adviceList={adviceList}
                  metricStats={metricStats}
                  reportArgs={reportArgs}
                  cluster={clusterInfoRes?.data}
                />
              </Collapse.Panel>
            ))}
          </Collapse>
        )}
      </div>
    </div>
  )
}

const MetricPanel: React.FC<{
  report: BenchmarkReport
  promqlSchema: PromqlSchemaMap
  adviceList: AdviceMap
  metricStats: MetricStatsMap
  reportArgs: ReportArgs
  cluster?: ClusterCluster
}> = ({
  report,
  cluster,
  promqlSchema,
  adviceList,
  metricStats,
  reportArgs
}) => {
  return (
    <>
      {!!report.description && <p>{report.description}</p>}
      {!!report.metricCategories?.length && (
        <List itemLayout='horizontal' split={false}>
          <div className='cate-header-wrap'>
            {report.metricCategories.map((cate) => {
              if (typeof cate.if === 'function' && !cate.if(metricStats)) {
                return null
              }

              return (
                <MetricCategory
                  cluster={cluster}
                  key={cate.id}
                  cate={cate}
                  promqlSchema={promqlSchema}
                  advices={adviceList[cate.id]}
                  metricStats={metricStats}
                  reportArgs={reportArgs}
                />
              )
            })}
          </div>
        </List>
      )}
      {!!report.children?.length && (
        <Collapse defaultActiveKey={report.children.map((r) => r.title)} ghost>
          {report.children.map((childReport) => (
            <Collapse.Panel
              key={childReport.title}
              header={<h4 className='mb-0'>{childReport.title}</h4>}
            >
              <MetricPanel
                report={childReport}
                promqlSchema={promqlSchema}
                adviceList={adviceList}
                metricStats={metricStats}
                reportArgs={reportArgs}
                cluster={cluster}
              />
            </Collapse.Panel>
          ))}
        </Collapse>
      )}
    </>
  )
}

export default ReportPageWrapper
